<template>
  <VdCard class="cohorts-list-item-component" size="small" expandable expanded>
    <div class="color" :style="{ backgroundColor: color }"></div>
    <div class="cohort">
      <div>
        <h4 class="cohort-name">
          <component :is="cohort.isControl || !isDraftAbtest ? 'div' : 'router-link'" :to="editLink">
            {{ cohort.name }}
          </component>
        </h4>
        <div class="cohort-id">
          Cohort ID:
          <span class="technical">{{ cohort.id }}</span>
        </div>
      </div>
      <div class="tail">
        <VdInput
          v-model="innerAllocationRate"
          class="allocation-input"
          type="integer"
          hide-controls
          :min="0"
          :max="100"
          suffix="%"
          @blur="onUpdateAllocationRate(innerAllocationRate)"
        />
        <vd-dropdown v-if="!cohort.isControl">
          <vd-dropdown-item v-if="!cohort.isControl && isDraftAbtest && !isDynamicAb" @click="onEdit">
            Edit
          </vd-dropdown-item>
          <vd-dropdown-item v-if="isDraftAbtest && !isDynamicAb" @click="onDuplicate">Duplicate</vd-dropdown-item>
          <vd-dropdown-item dangerous @click="onDelete">Delete</vd-dropdown-item>
        </vd-dropdown>
      </div>
    </div>
    <template #expanded-content>
      <CohortDetails
        :cohort="cohort"
        :ab-test-type="abTestType"
        :shops="shopsToDisplay"
        :is-dynamic-ab="isDynamicAb"
        :dynamic-segments="dynamicSegments"
        :ab-test-id="abtestId"
        :ab-test-status="abTestStatus"
        :offers-by-id="offersById"
        :campaign-names-by-id="campaignNamesById"
      />
    </template>
  </VdCard>
</template>

<script setup lang="ts">
import { VdCard, VdInput } from "@voodoo.io/voodoo-ui";
import { PropType, ref } from "vue";
import { useRouter } from "vue-router";

import CohortDetails from "@/components/CohortDetails/CohortDetails.vue";
import { CohortFragment } from "@/graphql";
import { TreatmentOfferSummary } from "@/modules/Offers/hooks/useTreatmentOffersData";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import links from "@/router/links";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  abTestStatus: {
    type: String,
    required: true,
  },
  cohort: {
    type: Object as PropType<CohortFragment>,
    required: true,
  },
  color: {
    type: String,
    default: "",
  },
  isDraftAbtest: {
    type: Boolean,
    required: true,
  },
  abTestType: {
    type: String,
    default: "class",
  },
  isDynamicAb: {
    type: Boolean,
    default: false,
  },
  dynamicSegments: {
    type: Array as PropType<{ id: string; name: string }[]>,
    default: () => [],
  },
  // Offer/campaign lookups fetched once at page level (useTreatmentOffersData), forwarded to CohortDetails.
  offersById: {
    type: Object as PropType<Record<string, TreatmentOfferSummary>>,
    default: () => ({}),
  },
  campaignNamesById: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
});

const emit = defineEmits(["update-allocation-rate", "delete", "duplicate"]);

const router = useRouter();
/**
 * Handle allocation rates
 */
const innerAllocationRate = ref(props.cohort.allocationRate);

/**
 * Watch the allocation rate value. If the allocationRate is undefined,
 * force the value to zero. Otherwise, emit the update-allocation-rate event.
 */
const onUpdateAllocationRate = (rate?: number) => {
  if (rate == null) {
    innerAllocationRate.value = 0;
  } else {
    emit("update-allocation-rate", { cohortId: props.cohort.id, allocationRate: rate });
  }
};

/**
 * Routing and action menu
 */
const editLink = {
  name: links.games.abtests.cohorts.edit,
  params: {
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    cohortId: props.cohort.id,
  },
};

const onEdit = () => {
  router.push(editLink);
};

const onDelete = () => {
  emit("delete", props.cohort.id);
};

const onDuplicate = () => {
  emit("duplicate", props.cohort.id);
};

const { shopsToDisplay } = useShopsManager(props.gameId, props.versionId);
</script>

<style lang="scss">
.cohorts-list-item-component {
  &.vd-card {
    background-color: var(--voodootune-background-color) !important;
  }

  .color {
    height: 40px;
    width: 4px;
    border-radius: 10px;
    margin-right: size("ML");
  }

  .cohort {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .tail {
      display: flex;
      align-items: center;

      .allocation-input {
        margin-right: size("M");

        .el-input__inner {
          width: 60px;
        }
      }
    }
  }

  h4.cohort-name {
    margin-bottom: size("S");
    font-weight: 600;
  }

  .cohort-id {
    font-size: size("M");
  }

  .technical {
    @extend %technical-name;
    color: $slate;
    font-weight: 200;
  }

  .vd-card__content {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  .no-override {
    font-style: italic;
    font-size: font("S");
  }

  .overridden-class-name {
    margin-bottom: size("ML");
  }

  .overridden-values-table {
    font-size: font("S");
    margin-bottom: size("XXL");

    .overridden .attribute-value {
      background-color: $cloudy-blue;
    }

    .is-na {
      color: $red-alert;
      opacity: 0.7;
    }
  }

  .overridden-class-attributes {
    overflow-y: scroll;
    max-height: 200px;
    font-size: font("S");

    .inner-content {
      display: grid;
      grid-template-columns: repeat(3, 1fr);

      .attribute-item {
        padding: size("M");

        .attribute-name {
          font-weight: 600;
          margin-bottom: 7.5px;
        }
      }

      .attribute-item:nth-of-type(6n + 4),
      .attribute-item:nth-of-type(6n + 5),
      .attribute-item:nth-of-type(6n + 6) {
        background: $foggy-blue;
      }
    }
  }
}
</style>
